import { useState } from "react";
import InteriorViewer from "./InteriorViewer";
import ExteriorViewer from "./ExteriorViewer";
import { buildingViews, exteriorScenes, interiorScenes } from "../data/scenes";
import FloorPlanViewer from "./FloorPlanViewer";
import HomeViewer from "./HomeViewer";
import { Home, Globe, Sofa, Ruler, FileText, Phone, RectangleGoggles } from "lucide-react";
import BrochureViewer from "./BrochureViewer";
import ContactViewer from "./ContactViewer";
// import BuildingViewer from "./BuildingViewer";
// import PolygonMaker from "./PolygoneMarker";

export default function Viewer360() {
  const [mode, setMode] = useState("building"); // building, buildingViewer, exterior, interior, floorPlan, brochure, contact
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [exteriorSceneKey, setExteriorSceneKey] = useState("exterior01");
  const [interiorSceneKey, setInteriorSceneKey] = useState("interior01");

  // 🎯 switch mode
  const switchMode = (e, newMode) => {
    e?.preventDefault();
    setMode(newMode);

    const targetScenes =
      newMode === "interior" ? interiorScenes : exteriorScenes;

    const firstScene = Object.keys(targetScenes)[0];

    if (firstScene) {
      newMode === "interior"
        ? setInteriorSceneKey(firstScene)
        : setExteriorSceneKey(firstScene);
    }

    setIsSidebarOpen(false)
  };

  return (
    <div style={{
        position: "relative",
        width: "100%",
        height: "100dvh",
        overflow: "hidden",
    }}>
      {/* 🎥 VIEWERS */}
      {mode === "interior" && (
        <InteriorViewer
          key={`interior-${interiorSceneKey}`}
          sceneKey={interiorSceneKey}
          setSceneKey={setInteriorSceneKey}
          scenes={interiorScenes}
        />
      )}

      {mode === "exterior" && (
        <ExteriorViewer
          key={`exterior-${exteriorSceneKey}`}
          sceneKey={exteriorSceneKey}
          setSceneKey={setExteriorSceneKey}
          scenes={exteriorScenes}
        />
      )}

      {mode === "floorPlan" && <FloorPlanViewer />}
      {mode === "building" && <HomeViewer views={buildingViews} />}
      {mode === "brochure" && <BrochureViewer />}
      {mode === "contact" && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            height: "100dvh",
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            zIndex: 1,
          }}
        >
          <ContactViewer />
        </div>
      )}

      {/* 🏷️ LOGO */}
      <div
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          zIndex: 30,
        }}
      >
        <img
          src="/logo.png"
          alt="Logo"
          style={{ height: 70, cursor: "pointer" }}
          onClick={(e) => switchMode(e, "building")}
        />
      </div>

      {/* ☰ OPEN BUTTON */}
      {!isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(true)}
          style={{
            position: "absolute",
            top: 10,
            right: 20,
            zIndex: 1000,
            width: 55,
            height: 55,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 24,
            cursor: "pointer",
            backdropFilter: "blur(6px)",
          }}
        >
          ☰
        </div>
      )}

      {/* 🌑 OVERLAY */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(0,0,0,0.15)",
            zIndex: 998,
          }}
        />
      )}

      {/* 🌙 SIDEBAR */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          height: "100vh",
          width: 110,
          background: "rgba(0, 0, 0, 0.35)", // transparent dark
          backdropFilter: "blur(10px)",      // glass blur effect
          WebkitBackdropFilter: "blur(10px)", // Safari support
          borderLeft: "1px solid rgba(255,255,255,0.1)", // subtle edge
          display: "flex",
          flexDirection: "column",
          zIndex: 999,
          borderTopLeftRadius: 20,
          borderBottomLeftRadius: 20,
          overflow: "hidden",

          // ✨ animation
          transform: isSidebarOpen
            ? "translateX(0) scale(1)"
            : "translateX(100%) scale(0.95)",
          transition: "transform 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55)",
          transformOrigin: "right center",
        }}
      >
        {/* ❌ CLOSE */}
        <div
          style={{
            paddingTop: 20,
            paddingBottom: 20,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            onClick={() => setIsSidebarOpen(false)}
            style={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: "#ff3b3b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 26,
              color: "#fff",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            }}
          >
            ✕
          </div>
        </div>

        {/* 📜 MENU */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 22,
            paddingBottom: 30,
            paddingTop: 10,
          }}
          className="hide-scrollbar"
        >
          {[
            { key: "building", label: "Home", icon: <Home size={22} /> },
            { key: "exterior", label: "Exterior", icon: <Globe size={22} /> },
            { key: "interior", label: "Interior", icon: <Sofa size={22} /> },
            { key: "floorPlan", label: "Floor Plans", icon: <Ruler size={22} /> },
            { key: "brochure", label: "Brochure", icon: <FileText size={22} /> },
            { key: "contact", label: "Contact", icon: <Phone size={22} /> },
          ].map((item) => {
            const isActive = mode === item.key;

            return (
              <div
                key={item.key}
                onClick={(e) => switchMode(e, item.key)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    background: isActive
                      ? "#0b1225"
                      : "rgba(255,255,255,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 26,
                    border: isActive
                      ? "2px solid #fff"
                      : "1px solid rgba(255,255,255,0.1)",
                    color: "#fff",
                    transition: "all 0.25s ease",
                    transform: isActive
                      ? "scale(1.05)"
                      : "scale(1)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {item.icon}
                  </div>
                </div>

                <div
                  style={{
                    fontSize: 12,
                    textAlign: "center",
                    color: "#fff",
                    opacity: isActive ? 1 : 0.7,
                  }}
                >
                  {item.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

}